import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from './auth';
import { useI18n, type PortalLanguage } from './i18n';
import { useApiQuery } from './query';
import type { Me } from './types';
import { useDisplay, type ThemePreference } from './theme';

// Préférences d'affichage du compte (langue, thème, menu replié) : appliquées aussitôt sur
// l'appareil, enregistrées sur le compte pour être retrouvées sur un autre poste.

export interface Preferences {
  language: PortalLanguage;
  theme: ThemePreference;
  sidebarCollapsed: boolean;
}

/**
 * Changement fait sur cet appareil pendant la session : il prime sur les préférences du compte
 * reçues ensuite (réponse de `/v1/me` arrivée après un choix de l'utilisateur).
 */
let changedLocally = false;

/**
 * Enregistrements sur le compte, un à la fois : l'API complète les préférences existantes, deux
 * envois simultanés (thème puis langue en une seconde) s'écraseraient. Les changements faits
 * pendant un envoi sont regroupés dans le suivant, qui part dès la réponse.
 */
let queued: Partial<Preferences> | null = null;
let inFlight: Promise<void> | null = null;

function save(patch: Partial<Preferences>, send: (p: Partial<Preferences>) => Promise<void>) {
  queued = { ...queued, ...patch };
  inFlight ??= (async () => {
    while (queued) {
      const body = queued;
      queued = null;
      await send(body).catch(() => {
        // Préférence appliquée sur l'appareil ; l'enregistrement sur le compte réessaiera.
      });
    }
    inFlight = null;
  })();
  return inFlight;
}

/** Préférences courantes et modification (appareil, puis compte si la session est ouverte). */
export function usePreferences() {
  const { language, setLanguage } = useI18n();
  const display = useDisplay();
  const auth = useAuth();
  const client = useQueryClient();
  const signedIn = auth.state.status === 'authenticated';

  const update = useCallback(
    (patch: Partial<Preferences>) => {
      changedLocally = true;
      if (patch.language) setLanguage(patch.language);
      if (patch.theme) display.setTheme(patch.theme);
      if (patch.sidebarCollapsed !== undefined) display.setSidebarCollapsed(patch.sidebarCollapsed);
      if (!signedIn) return Promise.resolve();
      return save(patch, (preferences) =>
        auth.api
          .request<Me>('PATCH', '/v1/me', { body: { preferences } })
          .then((me) => void client.setQueryData(['/v1/me', {}], me)),
      );
    },
    [setLanguage, display, signedIn, auth.api, client],
  );

  return {
    preferences: {
      language,
      theme: display.theme,
      sidebarCollapsed: display.sidebarCollapsed,
    } satisfies Preferences,
    update,
  };
}

/** À l'ouverture de session, applique les préférences enregistrées sur le compte. */
export function useSyncPreferences() {
  const { setLanguage } = useI18n();
  const { setTheme, setSidebarCollapsed } = useDisplay();
  const me = useApiQuery<Me>('/v1/me');
  const applied = useRef<string | null>(null);

  useEffect(() => {
    const prefs = me.data?.preferences;
    if (!prefs || applied.current === me.data?.id) return;
    // Autre compte dans le même onglet : les choix faits par le précédent ne comptent plus.
    if (applied.current !== null) changedLocally = false;
    applied.current = me.data?.id ?? null;
    if (changedLocally) return;
    setLanguage(prefs.language);
    setTheme(prefs.theme);
    setSidebarCollapsed(prefs.sidebarCollapsed);
  }, [me.data, setLanguage, setTheme, setSidebarCollapsed]);

  return me.data;
}
